import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { chunk, isEmpty } from 'lodash';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { v4 as uuidV4 } from 'uuid';
import PropTypes from 'prop-types';

import {
    setFileViewModalDetails,
    setLoaderProgress,
    setLoaderVisibility,
    setShowProgress,
} from '../../../redux';
import { TextArea } from './TextArea';
import { RadioSelector } from './RadioSelector';
import {
    DOCUMENT_REQUEST_FORM,
    DOCUMENT_REQUEST_FORM as DRF,
    MAX_LENGTH,
    QUERY_KEYS,
    QUERY_STALE_TIME,
    SUCCESS,
} from '../../../constants';
import {
    getAllCustomer,
    getCustomerByID,
    getHiringClients,
    createDocumentRequest,
    getAllDocumentCategory,
    validateFiles,
    signedURL,
    popup,
    handleScroll,
} from '../../../utils';
import { IconPlus, DocImage, ViewIcon, FeedbackCross, ZipImage } from '../../../assets/svg';
import SSCSelect from '../../shared/SSCSelect';
import useWindowSize from '../../../hooks/useWindowSize';
import { useDebouncedValue } from '../../../hooks/useDebounceValue';

const FILE_LIMIT = 150;
const BATCH_SIZE = 10;
const OFFSET = 1;
const LIMIT = 50;
const SUPPORTED_ZIP_MEME = ['application/zip', 'application/x-zip-compressed'];

export const RequestBoxContainer = ({
    accountDetails = {},
    submitStatus,
    setSubmitStatus,
    setRes,
    cancelStatus,
    setCancelStatus,
    confirmUpload,
    setOpenConfirmationBox,
    setConfirmUpload,
    setDisable,
}) => {
    const dispatch = useDispatch();
    const { width } = useWindowSize();
    const queryClient = useQueryClient();

    const [selectedFiles, setSelectedFiles] = useState([]);
    const [fileNames, setFileNames] = useState([]);
    const [note, setNote] = useState('');

    const role = useSelector((s) => s.user.role);
    const [selectedCustomer, setSelectedCustomer] = useState(
        !isEmpty(accountDetails) ? accountDetails : null,
    );
    const [selectedDocumentCategory, setSelectedDocumentCategory] = useState('');
    const [selectedHiringClient, setSelectedHiringClient] = useState('');
    const [selectedTpa, setSelectedTpa] = useState([]);
    const [priority, setPriority] = useState('');

    // set searched key
    const [customerSearch, setCustomerSearch] = useState('');
    const [documentCategorySearch, setDocumentCategorySearch] = useState('');
    const [hiringClientSearch, setHiringClientSearch] = useState('');

    // scrolling
    const scrollBar = useRef(null);
    const [isScrolling, setIsScrolling] = useState(false);
    const csmCaseload = useSelector((s) => s.csmCaseload?.csmCaseloadData);

    // FETCH CUSTOMER
    const [debouncedSearch] = useDebouncedValue(customerSearch, 500);
    const customerQuery = useQuery({
        queryKey: [QUERY_KEYS.CUSTOMERS, debouncedSearch, csmCaseload?.id, csmCaseload?.role_code],
        queryFn: async () =>
            await getAllCustomer({
                offset: OFFSET,
                limit: LIMIT,
                search: debouncedSearch,
                id: csmCaseload?.id || '',
                role: csmCaseload?.role_code || '',
            }),
        select: (data) => data?.data || [],
        staleTime: QUERY_STALE_TIME.THIRTY_MIN,
    });
    if (customerQuery.isError) popup('error', customerQuery.error.message);

    // FETCH DOCUMENT CATEGORY
    const [debouncedDocumentCategorySearch] = useDebouncedValue(documentCategorySearch, 500);
    const documentCategoryQuery = useQuery({
        queryKey: [QUERY_KEYS.DOCUMENT_CATEGORY, debouncedDocumentCategorySearch],
        queryFn: async () =>
            await getAllDocumentCategory({
                offset: OFFSET,
                limit: LIMIT,
                search: debouncedDocumentCategorySearch || '',
            }),
        select: (data) => data?.data || [],
        staleTime: QUERY_STALE_TIME.THIRTY_MIN,
    });
    if (documentCategoryQuery.isError) popup('error', documentCategoryQuery?.error?.message);

    // FETCH CUSTOMER's PLATFORMS
    const tpaQuery = useQuery({
        queryKey: [QUERY_KEYS.CUSTOMER_BY_ID, selectedCustomer?.id],
        queryFn: async () =>
            await getCustomerByID({
                customerID: selectedCustomer?.id,
                fetchPlatform: true,
            }),
        select: (data) => data?.data[0]?.platforms || [],
        enabled: !!selectedCustomer?.id,
    });
    if (tpaQuery.isError) popup('error', tpaQuery.error.message);

    useEffect(() => {
        if (!isEmpty(tpaQuery.data) && selectedCustomer.id) {
            setSelectedHiringClient('');
            setSelectedTpa([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tpaQuery.data, selectedCustomer?.id]);

    // FETCH HIRING CLIENTS
    const [debouncedHiringClientSearch] = useDebouncedValue(hiringClientSearch, 500);
    const hiringClientQuery = useQuery({
        queryKey: [
            QUERY_KEYS.HIRING_CLIENTS,
            selectedCustomer?.id,
            selectedTpa[0]?.id,
            debouncedHiringClientSearch,
        ],
        queryFn: async () =>
            await getHiringClients({
                account_id: selectedCustomer?.id,
                platform_id: selectedTpa[0]?.id,
                search: debouncedHiringClientSearch,
            }),
        select: (data) => data?.data || [],
        enabled: !!selectedTpa[0]?.id && !!selectedCustomer?.id,
    });
    if (hiringClientQuery.isError) popup('error', hiringClientQuery.error.message);

    // if user confirm for upload this useEffect will Work
    useEffect(() => {
        if (confirmUpload === 1) {
            handleSubmitWithUpload();
        } else if (confirmUpload === 2) {
            setSelectedFiles([]);
            setFileNames([]);
            setConfirmUpload(0);
            popup('error', DOCUMENT_REQUEST_FORM.FILE.DECLINED);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [confirmUpload]);

    //  Default Handle submit in case Of no upload
    async function handleSubmit() {
        const documentRequestUuid = uuidV4();
        const requestData = {
            id: documentRequestUuid,
            account_id: selectedCustomer?.id,
            hiring_client_id: selectedHiringClient?.id,
            platforms: isEmpty(selectedTpa)
                ? null
                : selectedTpa.map((t) => ({ id: t.id, platform_name: t.name })),
            document_category_id: selectedDocumentCategory?.id,
            priority: priority?.toLowerCase(),
            notes: note,
        };
        try {
            if (fileNames.length) {
                setOpenConfirmationBox(true);
            } else {
                dispatch(setLoaderVisibility(true));
                const response = await createDocumentRequest({ data: requestData });
                afterSubmission(response);
            }
        } catch (error) {
            popup('error', error?.message);
        }
    }

    // IF Request Has Uploads
    async function handleSubmitWithUpload() {
        const documentRequestUuid = uuidV4();
        const requestData = {
            id: documentRequestUuid,
            account_id: selectedCustomer?.id,
            hiring_client_id: selectedHiringClient?.id,
            platforms: isEmpty(selectedTpa)
                ? undefined
                : selectedTpa.map((t) => ({ id: t.id, platform_name: t.name })),
            document_category_id: selectedDocumentCategory?.id,
            priority: priority?.toLowerCase(),
            notes: note,
        };

        try {
            dispatch(setLoaderVisibility(true));
            dispatch(setShowProgress(true));
            // 1. Get pre-signed URLs
            const response = await signedURL({
                data: {
                    account_id: selectedCustomer?.id,
                    doc_req_id: documentRequestUuid,
                    objects: fileNames,
                },
            });
            const filePathURL = [];
            const urlData = response?.data;
            let totalUploadedBytes = 0;
            let totalBytes = 0;

            // Total bytes
            for (const file of selectedFiles) {
                totalBytes += file.size;
            }

            // 2. Upload files in batch (10 at a time to avoid memory pressure)
            const uploadFileWithProgress = (file, urlInfo) => {
                return new Promise((resolve, reject) => {
                    const xhr = new XMLHttpRequest();
                    xhr.open('PUT', urlInfo.preSignedUrl, true);
                    xhr.setRequestHeader('Content-Type', urlInfo.contentType);

                    let lastLoadedFileSize = 0;
                    xhr.upload.onprogress = (e) => {
                        if (e.lengthComputable) {
                            const delta = e.loaded - lastLoadedFileSize;
                            lastLoadedFileSize = e.loaded;

                            totalUploadedBytes += delta;
                            const percent = Math.round((totalUploadedBytes / totalBytes) * 100);
                            dispatch(setLoaderProgress(percent));
                        }
                    };

                    xhr.onload = () => {
                        if (xhr.status === 200) {
                            filePathURL.push(urlInfo.filePath);
                            resolve();
                        } else {
                            reject(new Error(`Upload failed for file ${file.name}`));
                        }
                    };

                    xhr.onerror = () => reject(new Error(`XHR error on file ${file.name}`));

                    xhr.send(file);
                });
            };

            const fileChunks = chunk(selectedFiles, BATCH_SIZE);
            const urlChunks = chunk(urlData, BATCH_SIZE);
            for (let i = 0; i < fileChunks.length; i++) {
                const batch = fileChunks[i];
                const batchUrls = urlChunks[i];
                await Promise.all(
                    batch.map((file, index) => uploadFileWithProgress(file, batchUrls[index])),
                );
            }

            // 3. Create document request + Save uploaded file metadata into db
            const finalResponse = await createDocumentRequest({
                data: { ...requestData, filePathURL },
            });
            setFileNames([]);
            afterSubmission(finalResponse);
        } catch (error) {
            popup('error', error?.message);
        } finally {
            dispatch(setLoaderVisibility(false));
        }
    }

    const resetForm = () => {
        setSelectedCustomer('');
        setSelectedDocumentCategory('');
        setSelectedTpa([]);
        setSelectedHiringClient('');
        setPriority('');
        setNote('');
        setSelectedFiles([]);
    };

    // Default afterSubmission to show error or success and neutralize all states
    const afterSubmission = (response) => {
        if (response?.status === 'success') {
            dispatch(setLoaderVisibility(false));
            setRes(true);
            resetForm();
            setSubmitStatus(false);
            setConfirmUpload(0);
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.DOCUMENTS], exact: false });
        } else {
            popup('error', response.message);
        }
    };

    useEffect(() => {
        if (submitStatus) {
            if (!selectedCustomer || !selectedDocumentCategory || !priority) {
                popup('error', DOCUMENT_REQUEST_FORM.EMPTY_FIELDS);
                setSubmitStatus(false);
                return;
            }
            handleSubmit();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [priority, selectedCustomer, selectedDocumentCategory, submitStatus]);

    const onFileChange = (event) => {
        try {
            const files = event.target.files;
            validateFiles(
                files,
                selectedFiles,
                DOCUMENT_REQUEST_FORM.FILE.FILE_SIZE_LIMIT_25MB,
                FILE_LIMIT,
                SUPPORTED_ZIP_MEME,
            );
            const selectedFilesArray = Array.from(files);
            const selectedFileNames = selectedFilesArray?.map((file) => file.name);
            setSelectedFiles((prevSelectedFiles) => [...prevSelectedFiles, ...selectedFilesArray]);
            setFileNames((prevFileNames) => [...prevFileNames, ...selectedFileNames]);
        } catch (error) {
            popup('error', error.message);
        }
    };

    // 1.) To dynamically creates the <input> element,
    // 2.) Don't need the input to persist in the DOM but only need its functionality.
    const openFileExplorer = () => {
        if (selectedFiles.length >= FILE_LIMIT) {
            popup(SUCCESS.SUCCESS_TOAST, `Maximum ${FILE_LIMIT} files allowed`);
        } else {
            const input = document.createElement('input');
            input.type = 'file';
            input.multiple = true;
            input.onchange = onFileChange;
            input.click();
        }
    };
    const handleRemoveFile = (indexToRemove) => {
        setSelectedFiles((prevSelectedFiles) =>
            prevSelectedFiles?.filter((_, index) => index !== indexToRemove),
        );
        setFileNames((prevFileNames) =>
            prevFileNames?.filter((_, index) => index !== indexToRemove),
        );
    };

    useEffect(() => {
        if (cancelStatus) {
            dispatch(setLoaderVisibility(false));
            resetForm();
            setCancelStatus(false);
        }
        // reset form if its csm or if csm caseload id changes
        if ((isEmpty(csmCaseload) && role === 'csm') || !!csmCaseload?.id) {
            resetForm();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cancelStatus, csmCaseload?.id]);

    useEffect(() => {
        setCustomerSearch('');
    }, [selectedCustomer]);

    const handleScrolling = () => {
        handleScroll(setIsScrolling, scrollBar, 2000);
    };

    useEffect(() => {
        if (
            !isEmpty(selectedCustomer) &&
            !isEmpty(selectedDocumentCategory) &&
            !isEmpty(priority)
        ) {
            setDisable(false);
        } else {
            setDisable(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCustomer, selectedDocumentCategory, priority]);

    const handlePreview = async (i) => {
        const currentFile = selectedFiles[i];
        const fileType = currentFile?.type;
        const uri = URL.createObjectURL(currentFile);
        dispatch(
            setFileViewModalDetails({
                openFileViewerModal: true,
                docs: [{ uri, fileType }],
                fileName: currentFile?.name,
            }),
        );
    };

    return (
        <div className='doc-request-box col-12 pb-2' id='doc-request-box'>
            <form
                action=''
                onScroll={handleScrolling}
                className={` pb-2 ${isScrolling ? 'scrolling' : 'not-scrolling'}`}
            >
                <div className='d-flex flex-column gap-2'>
                    <div className='row gap-2 gap-lg-0'>
                        <div className='col-12 col-lg-6 '>
                            <SSCSelect
                                required
                                placeholder={DRF.CUSTOMER.PLACEHOLDER}
                                label={DRF.CUSTOMER.LABEL}
                                options={customerQuery.data?.map((data) => ({
                                    label: data.name,
                                    value: data,
                                }))}
                                onChange={(v) => {
                                    setSelectedCustomer(!isEmpty(v) ? v?.value : null);
                                    setSelectedTpa([]);
                                }}
                                onInputChange={(v) => {
                                    if (v) {
                                        setCustomerSearch(v);
                                    }
                                }}
                                isClearable={true}
                                value={
                                    selectedCustomer
                                        ? { label: selectedCustomer?.name, value: selectedCustomer }
                                        : null
                                }
                                menuPortalTarget={document.getElementById('root')}
                                isLoading={customerQuery.isFetching}
                                isOptionSelected={(option) =>
                                    option.value?.id === selectedCustomer?.id
                                }
                            />
                        </div>
                        <div className='col-12 col-lg-6'>
                            <SSCSelect
                                placeholder={DRF.PLATFORM.PLACEHOLDER}
                                label={DRF.PLATFORM.LABEL}
                                isMulti={true}
                                options={tpaQuery.data
                                    ?.filter((t) => {
                                        const ids = selectedTpa.map((t) => t.id);
                                        return !ids.includes(t.id);
                                    })
                                    ?.map((data) => ({
                                        label: data?.name,
                                        value: data,
                                    }))}
                                onChange={(selected) => {
                                    if (selected && selected.length == 1) {
                                        setSelectedTpa(selected.map((u) => u.value));
                                    } else if (selected.length > 1) {
                                        setSelectedTpa(selected.map((u) => u.value));
                                        setSelectedHiringClient(null);
                                    } else {
                                        setSelectedTpa([]);
                                        setSelectedHiringClient(null);
                                    }
                                }}
                                isClearable={true}
                                menuPlacement={width < 768 ? 'top' : 'auto'}
                                menuPortalTarget={document.getElementById('root')}
                                value={
                                    selectedTpa.length > 0
                                        ? selectedTpa.map((e) => ({
                                              label: e?.name,
                                              value: e,
                                          }))
                                        : null
                                }
                                isDisabled={isEmpty(selectedCustomer) || isEmpty(tpaQuery.data)}
                            />
                        </div>
                    </div>
                    <div className='row gap-2 gap-lg-0'>
                        <div className='col-12 col-lg-6'>
                            <SSCSelect
                                required
                                label={DRF.DOCUMENT_CATEGORY.LABEL}
                                placeholder={DRF.DOCUMENT_CATEGORY.PLACEHOLDER}
                                options={documentCategoryQuery.data?.map((data) => ({
                                    label: data?.name,
                                    value: data,
                                }))}
                                onChange={(v) => setSelectedDocumentCategory(v?.value)}
                                onInputChange={(v) => {
                                    if (v) {
                                        setDocumentCategorySearch(v);
                                    }
                                }}
                                isClearable={true}
                                value={
                                    selectedDocumentCategory
                                        ? {
                                              label: selectedDocumentCategory?.name,
                                              value: selectedDocumentCategory,
                                          }
                                        : null
                                }
                                menuPortalTarget={document.getElementById('root')}
                                isLoading={documentCategoryQuery.isFetching}
                            />
                        </div>
                        <div className='col-12 col-lg-6'>
                            <SSCSelect
                                label={DRF.HIRING_CLIENT.LABEL}
                                placeholder={DRF.HIRING_CLIENT.PLACEHOLDER}
                                options={hiringClientQuery.data?.map((data) => ({
                                    label: data?.name,
                                    value: data,
                                }))}
                                onChange={(v) =>
                                    setSelectedHiringClient(!isEmpty(v) ? v?.value : null)
                                }
                                onInputChange={(v) => {
                                    if (v) {
                                        setHiringClientSearch(v);
                                    }
                                }}
                                isClearable={true}
                                menuPlacement={width < 768 ? 'top' : 'auto'}
                                menuPortalTarget={document.getElementById('root')}
                                value={
                                    selectedHiringClient
                                        ? {
                                              label: selectedHiringClient?.name,
                                              value: selectedHiringClient,
                                          }
                                        : null
                                }
                                isDisabled={isEmpty(selectedTpa) || selectedTpa.length > 1}
                                isLoading={hiringClientQuery.isFetching}
                            />
                        </div>
                    </div>
                    <div className='row gap-2 gap-md-0'>
                        <div className='col-12 col-md-6'>
                            <TextArea
                                label={DRF.NOTES.LABEL}
                                placeholder={DRF.NOTES.PLACEHOLDER}
                                note={note}
                                setNote={setNote}
                                maxLength={MAX_LENGTH.DOCUMENT_NOTES}
                                mainClass='w-100'
                                style={{ maxHeight: 86 }}
                                rows={3}
                            />
                        </div>
                        <div className='col-12 col-md-6'>
                            <div className='d-flex align-items-center justify-content-between'>
                                <RadioSelector
                                    required
                                    label={DRF.RADIO.LABEL}
                                    options={DRF.RADIO.OPTIONS}
                                    selectedData={priority}
                                    setSelectedData={setPriority}
                                    mainClass={'mb-2'}
                                />
                                {selectedFiles.length > 0 && (
                                    <span
                                        className={`pt-2 ${selectedFiles.length === FILE_LIMIT && 'text-danger'}`}
                                        style={{ fontSize: 'var(--font-15)' }}
                                    >
                                        {selectedFiles.length} Files
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className='d-flex align-items-center justify-content-start mt-0 mt-lg-2'>
                        {selectedFiles?.length === 0 && (
                            <button
                                className='ssc-primary-green-btn upload-file-btn rounded-1 mt-2 ms-1'
                                style={{ fontSize: '15px' }}
                                type='button'
                                onClick={openFileExplorer}
                                disabled={selectedFiles.length >= FILE_LIMIT}
                                title={DRF.FILE.UPLOAD}
                            >
                                {DRF.FILE.UPLOAD}
                            </button>
                        )}
                        {selectedFiles?.length > 0 && (
                            <div className='d-flex align-items-center file-upload mb-2'>
                                {selectedFiles?.map((file, index) => {
                                    const isZip = SUPPORTED_ZIP_MEME.includes(file?.type);
                                    return (
                                        <div
                                            key={index}
                                            className='doc-image-container d-flex align-items-start flex-column position-relative'
                                        >
                                            {isZip ? <ZipImage/> : <DocImage/>}
                                            {file.name.length < 10 ? (
                                                <span
                                                    title={file.name}
                                                    className='mt-2'
                                                    style={{ fontSize: '13px' }}
                                                >
                                                    {file.name}
                                                </span>
                                            ) : (
                                                <span
                                                    className='truncate-text mt-2'
                                                    title={file.name}
                                                    style={{ fontSize: '13px' }}
                                                >
                                                    {file.name.slice(0, 5) +
                                                        '...' +
                                                        file.name.split('.')[1]}
                                                </span>
                                            )}
                                            <button
                                                type='button'
                                                id='action-btn'
                                                title='Remove'
                                                className='position-absolute'
                                                style={{
                                                    top: '2px',
                                                    left: '2px',
                                                    background: 'white',
                                                }}
                                                onClick={() => handleRemoveFile(index)}
                                            >
                                                <FeedbackCross />
                                            </button>
                                            {!isZip && (
                                                <button
                                                    type='button'
                                                    id='action-btn'
                                                    title='View'
                                                    className='position-absolute'
                                                    style={{
                                                        bottom: '30px',
                                                        right: '2px',
                                                        background: 'white',
                                                    }}
                                                    onClick={() => handlePreview(index)}
                                                >
                                                    <ViewIcon />
                                                </button>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                        {selectedFiles?.length > 0 && (
                            <button
                                className='ssc-primary-green-btn add-upload-file-btn ms-2 mb-4'
                                type='button'
                                onClick={openFileExplorer}
                                disabled={selectedFiles.length >= FILE_LIMIT}
                            >
                                <IconPlus />
                            </button>
                        )}
                    </div>
                </div>
            </form>
        </div>
    );
};

RequestBoxContainer.propTypes = {
    accountDetails: PropTypes.object,
    submitStatus: PropTypes.bool,
    setSubmitStatus: PropTypes.func,
    setRes: PropTypes.func,
    cancelStatus: PropTypes.bool,
    setCancelStatus: PropTypes.func,
    confirmUpload: PropTypes.number,
    setOpenConfirmationBox: PropTypes.func,
    setConfirmUpload: PropTypes.func,
};
